import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

export const ApartmentsPage = ({ user, t, buildingId = null }) => {
  const navigate = useNavigate();
  const [apartments, setApartments] = useState([]);
  const [residents, setResidents] = useState([]);
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ unit_number: '', floor: 1 });
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin') {
      navigate('/app');
      return;
    }
    fetchData();
  }, [user, navigate, buildingId]);

  const fetchData = async () => {
    try {
      const aptsUrl = buildingId ? `${API}/apartments?building_id=${buildingId}` : `${API}/apartments`;
      const usersUrl = buildingId ? `${API}/users?building_id=${buildingId}` : `${API}/users`;
      const [aptsRes, usersRes] = await Promise.all([
        axios.get(aptsUrl, { withCredentials: true }),
        axios.get(usersUrl, { withCredentials: true }),
      ]);
      setApartments(aptsRes.data);
      setResidents((usersRes.data || []).filter((u) => u.role === 'resident'));
      const initForms = {};
      aptsRes.data.forEach((a) => {
        initForms[a.id] = {
          occupancy: a.tenant_id ? 'tenant' : 'owner',
          owner_id: a.owner_id || '',
          tenant_id: a.tenant_id || '',
        };
      });
      setForms(initForms);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (aptId, patch) => {
    setForms((prev) => ({ ...prev, [aptId]: { ...prev[aptId], ...patch } }));
  };

  // Returns a translation key for the validation error, or null if valid.
  const validateForm = (form) => {
    if (!form) return 'ownerRequired';
    if (!form.owner_id) return 'ownerRequired';
    if (form.occupancy === 'tenant') {
      if (!form.tenant_id) return 'tenantRequired';
      if (form.tenant_id === form.owner_id) return 'samePersonError';
    }
    return null;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const url = buildingId ? `${API}/apartments?building_id=${buildingId}` : `${API}/apartments`;
      await axios.post(url, formData, { withCredentials: true });
      toast.success(t('apartmentCreated'));
      setShowModal(false);
      setFormData({ unit_number: '', floor: 1 });
      fetchData();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  const handleDelete = async (apartmentId) => {
    if (!window.confirm(t('deleteApartment'))) return;
    try {
      await axios.delete(`${API}/apartments/${apartmentId}`, { withCredentials: true });
      toast.success(t('apartmentDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  const handleSave = async (apartmentId) => {
    const form = forms[apartmentId];
    const errKey = validateForm(form);
    if (errKey) {
      toast.error(t(errKey));
      return;
    }
    setSavingId(apartmentId);
    try {
      const base = `${API}/apartments/${apartmentId}/assign-parties`;
      const url = buildingId ? `${base}?building_id=${buildingId}` : base;
      await axios.post(
        url,
        {
          owner_id: form.owner_id || null,
          tenant_id: form.occupancy === 'tenant' ? (form.tenant_id || null) : null,
        },
        { withCredentials: true }
      );
      toast.success(t('assignmentSaved'));
      await fetchData();
    } catch (error) {
      const code = error?.response?.data?.detail;
      const map = {
        owner_tenant_same_person: 'samePersonError',
        tenant_requires_owner: 'tenantRequired',
      };
      toast.error(t(map[code] || 'operationFailed'));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('apartments')}</h1>
            </div>
            <button
              data-testid="create-apartment-button"
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors"
            >
              {t('newApartment')}
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apartments.map((apt) => {
            const form = forms[apt.id] || { occupancy: 'owner', owner_id: '', tenant_id: '' };
            const errKey = validateForm(form);
            const isTenant = form.occupancy === 'tenant';
            return (
              <div key={apt.id} className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg" data-testid={`apartment-${apt.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('unitNumber')} {apt.unit_number}</h3>
                    <p className="text-sm text-gray-600" style={{ fontFamily: 'Inter, sans-serif' }}>{t('floor')} {apt.floor}</p>
                  </div>
                  <button
                    onClick={() => handleDelete(apt.id)}
                    className="text-red-600 hover:text-red-700 text-sm"
                    data-testid={`delete-apartment-${apt.id}`}
                  >
                    {t('delete')}
                  </button>
                </div>

                {/* Occupancy selector */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t('occupancyStatus')}</label>
                  <div className="grid grid-cols-2 gap-2" data-testid={`occupancy-badge-${apt.id}`}>
                    <button
                      type="button"
                      data-testid={`occupancy-owner-${apt.id}`}
                      onClick={() => updateForm(apt.id, { occupancy: 'owner', tenant_id: '' })}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${!isTenant ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-white text-gray-500 border-gray-200'}`}
                    >
                      {t('ownerOccupied')}
                    </button>
                    <button
                      type="button"
                      data-testid={`occupancy-tenant-${apt.id}`}
                      onClick={() => updateForm(apt.id, { occupancy: 'tenant' })}
                      className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${isTenant ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-white text-gray-500 border-gray-200'}`}
                    >
                      {t('tenantOccupied')}
                    </button>
                  </div>
                </div>

                {/* Owner selector */}
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t('ownerLabel')} *</label>
                  <select
                    data-testid={`owner-select-${apt.id}`}
                    value={form.owner_id}
                    onChange={(e) => updateForm(apt.id, { owner_id: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700"
                  >
                    <option value="">{t('selectOwner')}</option>
                    {residents.map((r) => (
                      <option key={r._id} value={r._id}>{r.name}</option>
                    ))}
                  </select>
                </div>

                {/* Tenant selector (only when tenant-occupied) */}
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{t('tenantLabel')}{isTenant ? ' *' : ''}</label>
                  <select
                    data-testid={`tenant-select-${apt.id}`}
                    value={isTenant ? form.tenant_id : ''}
                    disabled={!isTenant}
                    onChange={(e) => updateForm(apt.id, { tenant_id: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${isTenant ? 'bg-white border-gray-200 text-gray-700' : 'bg-gray-100 border-gray-100 text-gray-400 cursor-not-allowed'}`}
                  >
                    <option value="">{t('selectTenantOptional')}</option>
                    {residents.map((r) => (
                      <option key={r._id} value={r._id}>{r.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-1">{t('ownerTenantHint')}</p>
                </div>

                {/* Validation message */}
                {errKey && (
                  <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-700" data-testid={`assignment-error-${apt.id}`}>
                    {t(errKey)}
                  </div>
                )}

                <button
                  type="button"
                  data-testid={`save-assignment-${apt.id}`}
                  disabled={!!errKey || savingId === apt.id}
                  onClick={() => handleSave(apt.id)}
                  className={`w-full px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${errKey || savingId === apt.id ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                >
                  {savingId === apt.id ? '...' : t('save')}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="create-apartment-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('newApartment')}</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('unitNumber')}</label>
                <input
                  type="text"
                  data-testid="unit-number-input"
                  value={formData.unit_number}
                  onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('floor')}</label>
                <input
                  type="number"
                  data-testid="floor-input"
                  value={formData.floor}
                  onChange={(e) => setFormData({ ...formData, floor: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border rounded-lg">{t('cancel')}</button>
                <button type="submit" data-testid="submit-apartment" className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg">{t('create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
